import React, { useState, useEffect } from "react";
import { Card, Form, Button, Space, Typography, Spin, InputNumber } from "antd";
import { FieldTimeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import { API_URL } from "../../shared/utils/Util";
import { getFetchT, fetchT } from "../../shared/utils/TypedUtil";
import { useAntdTheme, useNotification } from "../../shared/hooks";
import {
  WaitConfig,
  UpdateWaitConfigRequest,
  MAX_WAIT_CONFIG_MINUTES,
  MAX_WAIT_CONFIG_POINTS,
} from "./types";

const { Text, Paragraph } = Typography;

type WaitConfigEditorProps = {
  restaurantId: string;
};

export const WaitConfigEditor: React.FC<WaitConfigEditorProps> = ({ restaurantId }) => {
  const { t } = useTranslation();
  const { token } = useAntdTheme();
  const { updateSuccess, notifyError } = useNotification();
  const [config, setConfig] = useState<WaitConfig | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const refreshConfig = () => {
    getFetchT<WaitConfig>(
      `${API_URL}/v1/r/${restaurantId}/wait_config`,
      (result) => {
        setConfig(result);
        form.setFieldsValue({
          base_wait_minutes: result.base_wait_minutes,
          points_per_step: result.points_per_step,
          step_minutes: result.step_minutes,
          max_wait_minutes: result.max_wait_minutes,
          large_order_points: result.large_order_points,
        });
      },
      (err) => {
        notifyError(t("failed_to_fetch_wait_config"), err);
        setConfig({} as WaitConfig);
      }
    );
  };

  useEffect(() => {
    refreshConfig();
  }, [restaurantId]);

  const onFinish = (values: UpdateWaitConfigRequest) => {
    setLoading(true);

    fetchT<WaitConfig>(
      "PUT",
      `${API_URL}/v1/r/${restaurantId}/wait_config`,
      values,
      () => {
        updateSuccess(t("wait_config"));
        setLoading(false);
        refreshConfig();
      },
      (err) => {
        notifyError(t("failed_to_update_wait_config"), err);
        setLoading(false);
      }
    );
  };

  if (config === undefined) {
    return <Spin size="large" />;
  }

  return (
    <div>
      <Card
        title={
          <Space>
            <FieldTimeOutlined style={{ fontSize: 20, color: token.colorPrimary }} />
            <Text strong style={{ fontSize: token.fontSizeLG }}>
              {t("wait_config")}
            </Text>
          </Space>
        }
        style={{
          borderRadius: token.borderRadiusLG,
          boxShadow: token.boxShadowSecondary,
          border: `1px solid ${token.colorBorder}`,
        }}
        styles={{ body: { padding: token.paddingLG } }}
      >
        <Paragraph type="secondary">{t("wait_config_intro")}</Paragraph>

        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item
            name="base_wait_minutes"
            label={t("base_wait_minutes")}
            tooltip={t("base_wait_minutes_description")}
            rules={[
              {
                required: true,
                type: "number",
                min: 1,
                max: MAX_WAIT_CONFIG_MINUTES,
                message: t("wait_config_minutes_range", { max: MAX_WAIT_CONFIG_MINUTES }),
              },
            ]}
          >
            <InputNumber
              min={1}
              max={MAX_WAIT_CONFIG_MINUTES}
              step={1}
              precision={0}
              addonAfter={t("minutes")}
              style={{ width: "100%", maxWidth: 320 }}
            />
          </Form.Item>

          <Form.Item
            name="points_per_step"
            label={t("points_per_step")}
            tooltip={t("points_per_step_description")}
            rules={[
              {
                required: true,
                type: "number",
                min: 1,
                max: MAX_WAIT_CONFIG_POINTS,
                message: t("wait_config_points_range", { max: MAX_WAIT_CONFIG_POINTS }),
              },
            ]}
          >
            <InputNumber
              min={1}
              max={MAX_WAIT_CONFIG_POINTS}
              step={1}
              precision={0}
              addonAfter={t("load_points")}
              style={{ width: "100%", maxWidth: 320 }}
            />
          </Form.Item>

          <Form.Item
            name="step_minutes"
            label={t("step_minutes")}
            tooltip={t("step_minutes_description")}
            rules={[
              {
                required: true,
                type: "number",
                min: 0,
                max: MAX_WAIT_CONFIG_MINUTES,
                message: t("wait_config_step_minutes_range", { max: MAX_WAIT_CONFIG_MINUTES }),
              },
            ]}
          >
            <InputNumber
              min={0}
              max={MAX_WAIT_CONFIG_MINUTES}
              step={1}
              precision={0}
              addonAfter={t("minutes")}
              style={{ width: "100%", maxWidth: 320 }}
            />
          </Form.Item>

          <Form.Item
            name="max_wait_minutes"
            label={t("max_wait_minutes")}
            tooltip={t("max_wait_minutes_description")}
            dependencies={["base_wait_minutes"]}
            rules={[
              {
                required: true,
                type: "number",
                min: 1,
                max: MAX_WAIT_CONFIG_MINUTES,
                message: t("wait_config_minutes_range", { max: MAX_WAIT_CONFIG_MINUTES }),
              },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const base = getFieldValue("base_wait_minutes");
                  if (value != null && base != null && value < base) {
                    return Promise.reject(new Error(t("max_wait_must_be_gte_base")));
                  }
                  return Promise.resolve();
                },
              }),
            ]}
          >
            <InputNumber
              min={1}
              max={MAX_WAIT_CONFIG_MINUTES}
              step={1}
              precision={0}
              addonAfter={t("minutes")}
              style={{ width: "100%", maxWidth: 320 }}
            />
          </Form.Item>

          <Form.Item
            name="large_order_points"
            label={t("large_order_points")}
            tooltip={t("large_order_points_description")}
            rules={[
              {
                required: true,
                type: "number",
                min: 1,
                max: MAX_WAIT_CONFIG_POINTS,
                message: t("wait_config_points_range", { max: MAX_WAIT_CONFIG_POINTS }),
              },
            ]}
          >
            <InputNumber
              min={1}
              max={MAX_WAIT_CONFIG_POINTS}
              step={1}
              precision={0}
              addonAfter={t("load_points")}
              style={{ width: "100%", maxWidth: 320 }}
            />
          </Form.Item>

          <Form.Item noStyle shouldUpdate>
            {({ getFieldsValue }) => {
              const values: Partial<UpdateWaitConfigRequest> = getFieldsValue();
              const allFieldsSet =
                values.base_wait_minutes != null &&
                values.points_per_step != null &&
                values.step_minutes != null &&
                values.max_wait_minutes != null &&
                values.large_order_points != null;
              return allFieldsSet ? (
                <Text type="secondary">
                  {t("wait_config_summary", {
                    base: values.base_wait_minutes,
                    step: values.step_minutes,
                    pointsPerStep: values.points_per_step,
                    max: values.max_wait_minutes,
                    largeOrderPoints: values.large_order_points,
                  })}
                </Text>
              ) : null;
            }}
          </Form.Item>

          <Form.Item style={{ marginTop: token.marginLG, marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" loading={loading}>
              {t("save")}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
};
